import { CheckCircle2, ChevronDown, Circle, XCircle } from "lucide-react";
import { useState, type MouseEvent } from "react";
import { Button, Panel, StatusPill } from "../../components/primitives";
import { formatDate } from "../../lib/format";
import type { ChangeItem } from "../../lib/types";
import { PlanDiff, PlanDiffLegend } from "./PlanDiff";

/**
 * THE EDIT PLAN — accept / reject / rewrite every recommended change before the document is
 * created. This is the decision surface of /new, and it is the reason the screen exists.
 *
 * WHAT CHANGED, AND WHY (all measured on the live app at 390px):
 *
 *  - THE CARD TAX IS GONE. The prose sat inside FOUR nested rounded bordered boxes:
 *    section.panel > .edit-plan-panel > .edit-plan-card > .doc-diff-side. That left the
 *    document body 232px wide — 29 characters — on a 390px phone, and it is the mechanical
 *    cause of the misalignment: the panel header started 16px in from the panel edge and the
 *    document text started 45px in, so nothing shared a left column. There is now exactly ONE
 *    box: the Panel. Every row inside it takes --panel-pad, so every left edge lands on the
 *    same column BY CONSTRUCTION.
 *
 *  - ROWS ARE THE SAME DISCLOSURE STATE MACHINE AS /review, so the app has ONE mental model
 *    for one task. A PENDING row is force-open and CANNOT be collapsed — a reviewer must
 *    never be able to hide work they have not decided. A decided row collapses. Changing a
 *    verdict collapses the row immediately.
 *
 *  - AMBER IS RELEASED. `pending` used to render as a caution pill with a warning triangle,
 *    so a fresh plan opened as three amber hazard triangles before anything was wrong — in the
 *    same amber as the protected-fact chips on the same card, which is the ONE distinction
 *    this screen exists to make. An undecided proposal is the DEFAULT state, not a warning:
 *    it is a neutral pill, a hollow ring and the words "No decision", matching the GateState
 *    vocabulary on /review ("Not run"). The blocking signal does not weaken — it moves to
 *    where it belongs: a disabled primary carrying a plain-language reason, and a live count,
 *    both under the operator's thumb in the sticky action bar.
 *
 *  - BULK ACCEPT IS DEMOTED. "Accept all" was a full-width secondary button ABOVE the first
 *    edit: in a governance product, the bulk-approve was the most prominent, first-encountered
 *    control on the screen. It is a ghost action in the header now. Present, not prominent.
 *
 * NOTE FOR CONSOLIDATION: the `pending` verdict should become {tone:"neutral", glyph:"dot",
 * label:"No decision"} in src/lib/status.ts so /review's pills agree with these. It is done
 * locally here because lib/status.ts feeds a shared primitive owned by another workstream this
 * round. Nothing else in the app renders a `pending` change pill today.
 */

/** The undecided pill. Neutral, hollow ring, plain words. An undecided edit is not a hazard. */
function VerdictPill({ verdict }: { verdict: ChangeItem["verdict"] }) {
  if (verdict !== "pending") return <StatusPill status={verdict} />;
  return (
    <span
      className="status-pill tone-neutral"
      data-testid="status-pill"
      data-status="pending"
      data-tone="neutral"
      title="This edit has no decision yet."
    >
      <Circle aria-hidden="true" />
      No decision
    </span>
  );
}

const DECIDED_LINE: Record<Exclude<ChangeItem["verdict"], "pending">, string> = {
  approved: "Accepted — this text replaces the original when the document is created.",
  rejected: "Rejected — the original text is kept exactly as written."
};

export function EditPlanPanel({
  changes,
  generatedAt,
  pendingCount,
  discarded,
  onAcceptAll,
  onVerdict,
  onUpdateCopy
}: {
  changes: ChangeItem[];
  generatedAt: string;
  pendingCount: number;
  /** A setup change silently destroyed every decision. It still destroys them — it now says so. */
  discarded: boolean;
  onAcceptAll: () => void;
  onVerdict: (changeId: string, verdict: ChangeItem["verdict"]) => void;
  onUpdateCopy: (changeId: string, after: string) => void;
}) {
  /**
   * Which DECIDED rows the operator has re-opened. A pending row's open state is not stored
   * here at all: it is derived as `true`, unconditionally, so there is no code path by which
   * an undecided edit can be collapsed out of view.
   */
  const [reopened, setReopened] = useState<Record<string, boolean>>({});
  const [rewritten, setRewritten] = useState<Record<string, boolean>>({});

  if (!changes.length) {
    // The old idle panel restated the page's own lead ("Review every proposed text change
    // before the document is created") in a box that could not be acted on. It is gone. The
    // ONE thing worth saying here is the thing that used to happen SILENTLY.
    if (!discarded) return null;
    return (
      <Panel className="edit-plan-panel" padded data-testid="edit-plan-panel" data-state="discarded">
        <h2 className="plan-title">No edits proposed yet</h2>
        <p className="plan-note">
          The setup changed, so the previous edit plan was discarded. Review recommended edits again.
        </p>
      </Panel>
    );
  }

  function decide(change: ChangeItem, verdict: ChangeItem["verdict"]) {
    onVerdict(change.id, verdict);
    // A verdict change collapses the row immediately: the work is done, the next one is next.
    setReopened((current) => ({ ...current, [change.id]: false }));
  }

  function rewrite(change: ChangeItem, value: string) {
    onUpdateCopy(change.id, value);
    setRewritten((current) => ({ ...current, [change.id]: true }));
  }

  return (
    <Panel className="edit-plan-panel" data-testid="edit-plan-panel" data-state="ready">
      <div className="plan-header">
        <div className="plan-header-copy">
          <h2 className="plan-title">Recommended edits</h2>
          <p className="plan-meta">
            {changes.length} proposed
            {generatedAt ? ` · prepared ${formatDate(generatedAt)}` : ""}
          </p>
        </div>
        <div className="plan-header-actions">
          {/* REAL CHROME, STILL DEMOTED. It was a bare unstyled text link in the panel header —
              the least designed moment on the screen, sitting on a bulk-approve. A secondary
              button gives it a hairline and a ground (so it reads as the control it is) without
              ever competing with the per-edit decision or with the primary.

              IT COUNTS, SO WHEN THERE IS NOTHING LEFT TO COUNT IT IS NOT A BUTTON. It used to
              render disabled, reading "Accept all 0 remaining" with "Every edit already has a
              decision." underneath it: the zero was honest and the sentence was nonsense — a
              control offering to act on nothing. Once every edit has a verdict the bulk action has
              no work, so it is replaced by the plain statement of fact that was already sitting
              beneath it. No fabricated number, no dead control, nothing hidden. */}
          {pendingCount ? (
            <Button variant="secondary" size="sm" data-testid="edit-plan-accept-all" onClick={onAcceptAll}>
              Accept all {pendingCount} remaining
            </Button>
          ) : (
            <p className="plan-header-note">Every edit already has a decision.</p>
          )}
        </div>
      </div>

      <div className="plan-legend-row">
        <PlanDiffLegend />
      </div>

      <div className="plan-list">
        {changes.map((change, index) => {
          const pending = change.verdict === "pending";
          const open = pending || Boolean(reopened[change.id]);
          const decidedLine = change.verdict === "pending" ? "" : ` · ${DECIDED_LINE[change.verdict]}`;
          return (
            <details
              className="plan-row"
              data-testid="edit-plan-card"
              data-verdict={change.verdict}
              data-decided={pending ? "false" : "true"}
              key={change.id}
              open={open}
            >
              <summary
                className="plan-row-summary"
                onClick={(event: MouseEvent<HTMLElement>) => {
                  // FULLY CONTROLLED. The native toggle is always suppressed and React owns the
                  // open state, for two reasons: a PENDING row must never collapse (a reviewer
                  // may not hide work they have not decided), and a controlled `open` that the
                  // browser also mutates desynchronises from React — the native attribute sticks
                  // and the DOM the harness reads disagrees with the model. Suppressing the
                  // click keeps them in lockstep.
                  event.preventDefault();
                  if (pending) return;
                  setReopened((current) => ({ ...current, [change.id]: !open }));
                }}
              >
                <span className="plan-row-copy">
                  <span className="plan-row-title">
                    {index + 1}. {change.title}
                  </span>
                  <span className="plan-row-meta">
                    {change.page}
                    {decidedLine}
                  </span>
                </span>
                <VerdictPill verdict={change.verdict} />
                <ChevronDown className="plan-row-chevron" aria-hidden="true" />
              </summary>

              <div className="plan-row-body">
                {/* "Why" is now rendered INSIDE the diff (see PlanDiff): at >=1024 it fills the
                    left column beneath "Current text", which is what finally kills the half-empty
                    column every row carried. It is the same string, in the same reading order on
                    the phone, with no copy change. */}
                <PlanDiff
                  before={change.before}
                  after={change.after}
                  why={change.reason}
                  rewritten={Boolean(rewritten[change.id])}
                  rejected={change.verdict === "rejected"}
                  onAfterChange={(value) => rewrite(change, value)}
                  editLabel={`Rewrite the proposed text for ${change.title}`}
                  editTestId="edit-plan-after"
                />

                {/* THE TWO CONTROLS THE WHOLE SCREEN EXISTS FOR NOW CARRY HIERARCHY.
                    They were two identical 270px ghost outlines: the affirmative and the
                    destructive decision, visually indistinguishable, neither carrying any weight.
                    Accept takes the affirmative family the "Approved" pill already speaks (the
                    --green ramp) and it takes the room — it is the wide control. Reject stays a
                    quiet neutral hairline at its own width. Neither is a DEFAULT: the row starts
                    with no verdict, both are one click, and the chosen one seals to a full ring —
                    glyph, words, aria-pressed AND colour, never colour alone, never an edge rail. */}
                <div className="plan-decision">
                  <Button
                    className={`plan-accept ${change.verdict === "approved" ? "is-accepted" : ""}`}
                    variant="secondary"
                    data-testid="edit-plan-accept"
                    aria-pressed={change.verdict === "approved"}
                    onClick={() => decide(change, "approved")}
                  >
                    <CheckCircle2 aria-hidden="true" />
                    Accept
                  </Button>
                  <Button
                    className={`plan-reject ${change.verdict === "rejected" ? "is-rejected" : ""}`}
                    variant="secondary"
                    data-testid="edit-plan-reject"
                    aria-pressed={change.verdict === "rejected"}
                    onClick={() => decide(change, "rejected")}
                  >
                    <XCircle aria-hidden="true" />
                    Reject
                  </Button>
                </div>
              </div>
            </details>
          );
        })}
      </div>
    </Panel>
  );
}
